import { useMutation } from '@apollo/client';
import {
  IAnalysisPrefInput,
  IAnalysisSetPrefMutation,
  IAnalysisSetPrefMutationVariables,
  IUserAliasPrefInput,
  QueryType,
} from '../../../utils/apollo/graphqlSchema';
import { SET_ANALYSIS_PREF } from '../../../utils/apollo/analysis';
import { persistChartState } from '../../../redux/utils/chartPersistAndRestore';
import {
  getMeasureColorFromColorConfig,
  getSliceColorFromColorConfig,
  IAnalysisColorConfig,
} from '../utils/analysisColorConfig';
import {
  IDisplayedMeasureSetting,
  IDisplayedSliceSetting,
  IParsedAliasReplacements,
} from '../interfaces';
import { useAppDispatch } from '../../../redux/hooks';
import { updateChartSettings } from '../../../redux/slices/analyses';
import { COMPARISON_KEY_SUFFIX } from '../../../config/constants';
import { MaybeNil } from '../../../utils/interfaces';
import { isNil } from 'lodash';
import { useCallback } from 'react';
import { useChartContext } from '../ChartContext';

type AnalysisSetChartPrefHook = () => Promise<string[]>;

const mapDisplayedMeasureSettings = (
  measureSettings: IDisplayedMeasureSetting[],
  colorConfig: IAnalysisColorConfig,
) =>
  measureSettings.map((dms) => ({
    ...dms,
    // set a custom color from config
    color: getMeasureColorFromColorConfig(colorConfig, dms.key),
  }));

const mapDisplayedSliceSettings = (
  sliceSettings: IDisplayedSliceSetting[],
  colorConfig: IAnalysisColorConfig,
) =>
  sliceSettings
    // KBT-840: filter out comparison slices.
    .filter((dss) => !dss.key.endsWith(COMPARISON_KEY_SUFFIX))
    .map((dss) => ({
      ...dss,
      // set a custom color from config
      color: getSliceColorFromColorConfig(colorConfig, dss.key),
    }));

const mapParsedAliasesToInput = (
  parsedAliasReplacements: MaybeNil<IParsedAliasReplacements>,
): IUserAliasPrefInput[] | null => {
  if (isNil(parsedAliasReplacements)) {
    return null;
  }
  return Object.entries(parsedAliasReplacements).map(
    ([userAliasId, replacement]) => ({
      replacement,
      userAliasId,
    }),
  );
};

export const usePrepareChartPrefs = (analysisId: string) => {
  const {
    metadata: { queryType },
    settings: { chartSettings },
    colorConfig,
    chartData,
  } = useChartContext();
  const { activeIncidentTypes } = chartSettings;

  const dispatch = useAppDispatch();

  return useCallback(() => {
    let displayedMeasureSettings = chartSettings.displayedMeasureSettings;
    let displayedSliceSettings = chartSettings.displayedSliceSettings;

    if (colorConfig) {
      displayedMeasureSettings = mapDisplayedMeasureSettings(
        chartSettings.displayedMeasureSettings,
        colorConfig,
      );
      displayedSliceSettings = mapDisplayedSliceSettings(
        chartSettings.displayedSliceSettings,
        colorConfig,
      );
    }

    let newChartSettings = chartSettings;
    if (queryType === QueryType.Expression || queryType === QueryType.Funnel) {
      newChartSettings = {
        ...chartSettings,
        displayedMeasureSettings,
        displayedSliceSettings,
      };
    } else if (queryType === QueryType.Retention) {
      newChartSettings = {
        ...chartSettings,
        displayedSliceSettings,
      };
    }

    const aliasReplacements =
      chartData && 'aliasReplacements' in chartData
        ? chartData.aliasReplacements
        : null;

    return {
      pref: {
        chartPref: persistChartState({
          queryType,
          chartSettings: newChartSettings,
          activeIncidentTypes,
        }),
        aliases: mapParsedAliasesToInput(aliasReplacements),
      } satisfies IAnalysisPrefInput,
      commitChartSettings: () => {
        dispatch(
          updateChartSettings({
            analysisId,
            chartSettings: newChartSettings,
          }),
        );
      },
    };
  }, [
    activeIncidentTypes,
    analysisId,
    chartData,
    chartSettings,
    colorConfig,
    dispatch,
    queryType,
  ]);
};

/**
 * Custom hook to persist chart preferences
 */
const useAnalysisSetChartPref = (
  analysisId: string,
): AnalysisSetChartPrefHook => {
  const preparePrefInput = usePrepareChartPrefs(analysisId);

  const [setChartPref] = useMutation<
    IAnalysisSetPrefMutation,
    IAnalysisSetPrefMutationVariables
  >(SET_ANALYSIS_PREF);

  return useCallback(async () => {
    const { pref, commitChartSettings } = preparePrefInput();
    const result = await setChartPref({
      variables: {
        input: {
          analysisId,
          pref,
        },
      },
    });

    commitChartSettings();

    const errors = result.data?.analysisSetChartPref.errors ?? [];
    errors.forEach((e) => {
      console.warn(
        `Failed to set chart preferences for analysis ${analysisId}`,
        e,
      );
    });
    return errors.map((e) => e.messages.join(', '));
  }, [analysisId, preparePrefInput, setChartPref]);
};

export default useAnalysisSetChartPref;
